import React, { FC, FormEvent, useCallback, useEffect, useRef, useState } from 'react';
import styles from '../journey-explorer.module.scss';
import { useJourneyExplorerApiService } from '../../../../api/providers/AppServiceProvider';
import { SavedView, SavedViewState } from '../../../../models/journey-explorer/journey-explorer';
import { errorMessage, formatRelative } from '../journey-explorer-utils';
import { parseViewState } from '../journey-view-state';

const LOAD_ERROR = 'Saved views could not be loaded.';
const SAVE_ERROR = 'The view could not be saved.';
const DELETE_ERROR = 'The view could not be deleted.';

interface SavedViewsProps {
	current: SavedViewState | null;
	onOpen: (state: SavedViewState) => void;
}

const SavedViews: FC<SavedViewsProps> = ({ current, onOpen }) => {
	const apiService = useJourneyExplorerApiService();
	const apiRef = useRef(apiService);
	apiRef.current = apiService;
	const containerRef = useRef<HTMLDivElement>(null);
	const [open, setOpen] = useState(false);
	const [views, setViews] = useState<SavedView[] | null>(null);
	const [name, setName] = useState('');
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const load = useCallback(() => {
		setError(null);
		apiRef.current
			.getSavedViews()
			.then(setViews)
			.catch((reason) => setError(errorMessage(reason, LOAD_ERROR)));
	}, []);

	useEffect(() => {
		if (!open) {
			return;
		}
		load();
		const onDown = (event: MouseEvent) => {
			if (!containerRef.current?.contains(event.target as Node)) {
				setOpen(false);
			}
		};
		const onKey = (event: KeyboardEvent) => event.key === 'Escape' && setOpen(false);
		window.addEventListener('mousedown', onDown);
		window.addEventListener('keydown', onKey);
		return () => {
			window.removeEventListener('mousedown', onDown);
			window.removeEventListener('keydown', onKey);
		};
	}, [open, load]);

	const save = async (event: FormEvent) => {
		event.preventDefault();
		if (!current || !name.trim()) {
			return;
		}
		setBusy(true);
		setError(null);
		try {
			const saved = await apiRef.current.saveView(name.trim(), current);
			setViews((items) => [saved, ...(items ?? []).filter((item) => item.id !== saved.id)]);
			setName('');
		} catch (reason) {
			setError(errorMessage(reason, SAVE_ERROR));
		} finally {
			setBusy(false);
		}
	};

	const remove = async (view: SavedView) => {
		setError(null);
		try {
			await apiRef.current.deleteView(view.id);
			setViews((items) => (items ?? []).filter((item) => item.id !== view.id));
		} catch (reason) {
			setError(errorMessage(reason, DELETE_ERROR));
		}
	};

	const openView = (view: SavedView) => {
		const state = parseViewState(view.state);
		if (!state) {
			setError('This view was saved by an older version and cannot be opened.');
			return;
		}
		onOpen(state);
		setOpen(false);
	};

	return (
		<div ref={containerRef} className={styles.saved}>
			<button
				type="button"
				className={styles.compare_button}
				aria-expanded={open}
				onClick={() => setOpen((value) => !value)}
			>
				Saved views
			</button>
			{open && (
				<div className={styles.saved_popover} role="dialog" aria-label="Saved views">
					<div className={styles.compare_title}>Saved views</div>
					<div className={styles.compare_hint}>
						Save the current filters, dates and Flow question to come back to them, or to share with a
						client.
					</div>
					<form className={styles.saved_form} onSubmit={save}>
						<input
							className={styles.simplify_input}
							aria-label="View name"
							placeholder="Name this view"
							maxLength={100}
							value={name}
							onChange={(event) => setName(event.target.value)}
						/>
						<button
							type="submit"
							className={styles.compare_apply}
							disabled={busy || !current || !name.trim()}
						>
							{busy ? 'Saving…' : 'Save'}
						</button>
					</form>
					{error && <div className={styles.export_error}>{error}</div>}
					<div className={styles.saved_list}>
						{views === null && !error && <span className={styles.saved_empty}>Loading…</span>}
						{views?.length === 0 && <span className={styles.saved_empty}>No saved views yet.</span>}
						{views?.map((view) => (
							<div key={view.id} className={styles.saved_item}>
								<button type="button" className={styles.saved_open} onClick={() => openView(view)}>
									<span className={styles.saved_name}>{view.name}</span>
									<span className={styles.saved_when}>{formatRelative(view.updated_on)}</span>
								</button>
								<button
									type="button"
									className={styles.simplify_remove}
									aria-label={`Delete ${view.name}`}
									title="Delete"
									onClick={() => remove(view)}
								>
									✕
								</button>
							</div>
						))}
					</div>
				</div>
			)}
		</div>
	);
};

export default SavedViews;
